import clsx from 'clsx';
import Image from 'next/image';
import { resolveImageAltText } from '@/lib/utils';
import ExternalLink from '@/components/elements/ExternalLink';

/**
 * A dual CTA wayfinder block with two columns, each with an image background and
 * cta content.
 */
export default function WayfinderDualCta({ position, block }) {
  const { column_one, column_two, _metadata } = block.dual_cta;
  return (
    <section
      id={`${_metadata.uid}_${position}`}
      data-name="wayfinder-dual-cta"
      className={`relative overflow-hidden`}
    >
      <div className="w-full justify-center text-center sm:flex">
        <Column column={column_one} />
        <Column column={column_two} />
      </div>
    </section>
  );
}

/**
 * A single column in the dual CTA wayfinder block with an image background and
 * a title, description, and button.
 */
const Column = ({ column }) => (
  <div
    className={`relative h-97.5 px-1 py-15 sm:w-1/2 lg:h-150 lg:px-30 ${column?.background_overlay_on ? `bg-[${column.background_overlay_color}]` : `bg-[${column.background_color}]`}`}
  >
    {column.background_image && (
      <div
        className={clsx(`absolute top-0 left-0 h-full w-full`, {
          'z-10 opacity-70': column.background_overlay_on
        })}
      >
        <Image
          src={column.background_image.url}
          alt={resolveImageAltText(column.background_image)}
          fill
          sizes="(min-width: 640px) 50vw, 100vw"
          className="absolute top-0 left-0 z-0 max-h-150 object-cover"
        />
      </div>
    )}
    <div className="flex h-full flex-col justify-center">
      <h2
        className="h3 lg:h2 relative z-30 pb-5"
        style={{ color: column.text_color }}
      >
        {column.title}
      </h2>
      <div
        className="body-r lg:body-l relative z-30 pb-5"
        style={{ color: column.text_color }}
      >
        {column.description}
      </div>
      <div className="relative z-30">
        {(column.external_link || column?.button_link) && (
          <ExternalLink
            externalLink={column?.external_link}
            internalLink={column?.button_link[0]}
            linkType={column?.link_type}
            linkText={column?.button_text}
            linkStyling={`btn btn-${column?.button_style} mt-5 inline-block`}
          />
        )}
      </div>
    </div>
  </div>
);
